import React, { useEffect, useRef, useCallback } from 'react';

interface AudioWaveformProps {
  stream: MediaStream | null;
  isRecording: boolean;
  width?: number;
  height?: number;
  barColor?: string;
  backgroundColor?: string;
}

export function AudioWaveform({
  stream,
  isRecording,
  width = 200,
  height = 40,
  barColor = '#FF6B9D',
  backgroundColor = 'transparent',
}: AudioWaveformProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const animationRef = useRef<number | null>(null);
  const dataArrayRef = useRef<Uint8Array | null>(null);

  // 繪製波形
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const analyser = analyserRef.current;
    const dataArray = dataArrayRef.current;

    if (!canvas || !analyser || !dataArray) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // 獲取頻率數據
    analyser.getByteFrequencyData(dataArray as Uint8Array<ArrayBuffer>);

    // 清空畫布
    ctx.clearRect(0, 0, width, height);

    // 繪製背景
    if (backgroundColor !== 'transparent') {
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, width, height);
    }

    // 繪製條形
    const barCount = 16; // 條形數量
    const barWidth = (width / barCount) * 0.6;
    const gap = (width / barCount) * 0.4;
    const step = Math.floor(dataArray.length / barCount);

    // 錄音狀態使用主色，預覽狀態使用較淡的顏色
    ctx.fillStyle = isRecording ? barColor : '#6FEDD6';

    for (let i = 0; i < barCount; i++) {
      // 取樣頻率數據
      let value = dataArray[i * step] || 0;
      
      // 噪音門檻：忽略過小的信號 (避免 AGC 造成的底噪跳動)
      if (value < 10) value = 0;
      
      const percent = value / 255;
      const barHeight = Math.max(2, percent * height * 0.9);
      
      const x = i * (barWidth + gap) + gap / 2;
      const y = (height - barHeight) / 2;

      // 繪製圓角矩形
      const radius = Math.min(barWidth / 2, 3);
      ctx.beginPath();
      ctx.roundRect(x, y, barWidth, barHeight, radius);
      ctx.fill();
    }

    // 繼續動畫
    animationRef.current = requestAnimationFrame(draw);
  }, [width, height, barColor, backgroundColor, isRecording]);

  // 非活躍狀態顯示靜態波形
  const renderStaticWaveform = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, width, height);

    const barCount = 16;
    const barWidth = (width / barCount) * 0.6;
    const gap = (width / barCount) * 0.4;

    ctx.fillStyle = 'rgba(255, 255, 255, 0.2)'; // 靜態狀態的微弱顏色

    for (let i = 0; i < barCount; i++) {
      const barHeight = 4;
      const x = i * (barWidth + gap) + gap / 2;
      const y = (height - barHeight) / 2;

      ctx.beginPath();
      ctx.roundRect(x, y, barWidth, barHeight, 2);
      ctx.fill();
    }
  }, [width, height]);

  // 初始化音頻分析器 - 只要有 stream 就初始化
  useEffect(() => {
    if (!stream) {
      // 無 stream 時停止動畫並顯示靜態波形
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      renderStaticWaveform();
      return;
    }

    // 建立 AudioContext
    const audioContext = new AudioContext();
    audioContextRef.current = audioContext;

    // 建立分析器
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 64;
    analyser.smoothingTimeConstant = 0.8;
    analyserRef.current = analyser;

    // 連接音頻源
    const source = audioContext.createMediaStreamSource(stream);
    source.connect(analyser);

    // 建立數據陣列
    const bufferLength = analyser.frequencyBinCount;
    dataArrayRef.current = new Uint8Array(bufferLength);

    // 開始動畫
    animationRef.current = requestAnimationFrame(draw);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      audioContext.close();
    };
  }, [stream, draw, renderStaticWaveform]);

  // 初始渲染靜態波形
  useEffect(() => {
    if (!stream) {
      renderStaticWaveform();
    }
  }, [stream, renderStaticWaveform]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      className="block"
      style={{ width, height }}
    />
  );
}

